import axios from 'axios'
import store from './store'
import { AUTH_MODULE, SNACKBAR_MODULE, USER_STATISTICS_MODULE } from './store/modules'
import {
  AUTH_LOGOUT, SNACKBAR_OPEN,
  UPDATE_ADMIN_ROLE, UPDATE_DEBUG_VIEW, UPDATE_MANAGER_RESTRICTED,
  UPDATE_USER_PROFILE,
  UPDATE_USER_ROLE,
  USER_STATISTICS_UPDATE
} from './store/actions'
import { getSite } from './utils/path'
import { BackendEndpoints, BackendErrorCodes } from './enum'
import { BackendError } from './errors'

const getRouteErrors = () => ({
  [BackendEndpoints.MAP_DRIVER_PROFILE]: [
    {
      code: BackendErrorCodes.DATA_MISSING,
      missedFields: 'user_id',
      text: `$vuetify.errors.${BackendEndpoints.MAP_DRIVER_PROFILE}.user_id`
    },
    {
      code: BackendErrorCodes.ENTITY_NOT_FOUND,
      details: 'profile',
      text: `$vuetify.errors.${BackendEndpoints.MAP_DRIVER_PROFILE}.profile`
    },
    {
      code: BackendErrorCodes.ENTITY_NOT_FOUND,
      details: 'settings',
      text: `$vuetify.errors.${BackendEndpoints.MAP_DRIVER_PROFILE}.settings`
    }
  ],
  [BackendEndpoints.SETTINGS_FETCH_DEFAULT]: [
    {
      code: BackendErrorCodes.ENTITY_NOT_FOUND,
      text: `$vuetify.errors.${BackendEndpoints.SETTINGS_FETCH_DEFAULT}.default_settings`
    }
  ],
  [BackendEndpoints.SETTINGS_SYNC_DRIVERS]: [
    {
      code: BackendErrorCodes.ENTITY_NOT_FOUND,
      text: `$vuetify.errors.${BackendEndpoints.SETTINGS_FETCH_DEFAULT}.default_settings`
    }
  ]
})

const isConnectionRefused = (error) => {
  return !error.response || (error.response.hasOwnProperty('responseText') && error.response.request.responseText.includes('ECONNREFUSED'))
}

class BackendProvider {
  constructor () {
    // basic backend fetcher for most calls
    this.$axios = axios.create({
      baseURL: getSite(),
      /* timeout: 5000, */
      headers: { 'Content-Type': 'application/json' }
    })

    // axios instance for dynamic archive uploading

    this.$axiosBlob = axios.create({
      baseURL: getSite(),
      responseType: 'blob'
    })

    // set interceptors for basic axios instance
    this.$axios.interceptors.response.use(
      function (response) {
        if ('data' in response && typeof response.data === 'object') {
          if ('is_special' in response.data) {
            store.dispatch(
              `${AUTH_MODULE}/${UPDATE_USER_ROLE}`,
              response.data.is_special
            )
          }

          if ('is_admin' in response.data) {
            store.dispatch(
              `${AUTH_MODULE}/${UPDATE_ADMIN_ROLE}`,
              response.data.is_admin
            )
          }

          if ('is_debug_view' in response.data) {
            store.dispatch(
              `${AUTH_MODULE}/${UPDATE_DEBUG_VIEW}`,
              response.data.is_debug_view
            )
          }

          if ('manager_restricted' in response.data) {
            store.dispatch(
              `${AUTH_MODULE}/${UPDATE_MANAGER_RESTRICTED}`,
              response.data.manager_restricted
            )
          }

          if ('user_data' in response.data) {
            const dataGathered = Object.keys(response.data.user_data).length > 0

            if (dataGathered) {
              store.dispatch(
                `${AUTH_MODULE}/${UPDATE_USER_PROFILE}`,
                response.data.user_data
              )
            } else if (store.state[AUTH_MODULE].auth) {
              // need to logout if we haven't got any data from backend
              store.dispatch(`${AUTH_MODULE}/${AUTH_LOGOUT}`)
            }
          }

          if ('user_statistics' in response.data) {
            if (Object.keys(response.data.user_statistics).length > 0) {
              store.dispatch(`${USER_STATISTICS_MODULE}/${USER_STATISTICS_UPDATE}`, response.data.user_statistics)
            }
          }

          response.data = response.data.data
        }

        return response
      },
      function (error) {
        console.log(error)
        if (isConnectionRefused(error)) {
          store.dispatch(`${SNACKBAR_MODULE}/${SNACKBAR_OPEN}`, {
            text: '$vuetify.errors.connectionRefused',
            color: 'error'
          })

          return Promise.reject(error)
        }

        if (error.response.status === 401) {
          store.dispatch(`${AUTH_MODULE}/${AUTH_LOGOUT}`)
        }

        return Promise.reject(error)
      }
    )

    this.routeErrors = getRouteErrors()
  }

  getRouteErrorText (route, error) {
    const errorCode = error.code

    if (errorCode === BackendErrorCodes.PERMISSION_DENIED) {
      return '$vuetify.errors.permissionsError'
    }

    if (errorCode === BackendErrorCodes.INTERNAL_ERROR) {
      return '$vuetify.errors.generalError'
    }

    if (this.routeErrors.hasOwnProperty(route)) {
      const errors = this.routeErrors[route].filter(item => item.code === errorCode)

      if (errors.length === 1) {
        return errors[0].text
      }

      for (const errorEntry of errors) {
        if (errorEntry.hasOwnProperty('missedFields') && error.hasOwnProperty('missed_fields')) {
          if (error.missed_fields.includes(errorEntry.missedFields)) {
            return errorEntry.text
          }
        }

        if (errorEntry.hasOwnProperty('details') && error.hasOwnProperty('details')) {
          const details = error.details.toLocaleLowerCase()
          if (details.includes(errorEntry.details)) {
            return errorEntry.text
          }
        }
      }

      // TODO: check error status and missing fields, if needed
      return '$vuetify.errors.generalError'
    } else {
      return '$vuetify.errors.generalError'
    }
  }

  async _apiGet ({ url = '', params = null, type = 'basic' }) {
    const _axios = type === 'basic' ? this.$axios : this.$axiosBlob

    try {
      if (!params) {
        return await _axios.get(url)
      } else {
        return await _axios.get(url, { params })
      }
    } catch (e) {
      if (!isConnectionRefused(e)) {
        console.error(e)
        const error = e.response
        throw new BackendError(e.message, this.getRouteErrorText(url, error.data))
      }
    }
  }

  async _apiPost ({ url = '', data = null, type = 'basic' }) {
    const _axios = type === 'basic' ? this.$axios : this.$axiosBlob

    try {
      if (!data) {
        return await _axios.post(url)
      } else {
        return await _axios.post(url, data)
      }
    } catch (e) {
      if (!isConnectionRefused(e)) {
        console.error(e)
        const error = e.response
        throw new BackendError(e.message, this.getRouteErrorText(url, error.data))
      }
    }
  }

  // TODO: send specific models as function parameters !

  async googleLogin (data) {
    return this._apiPost({ url: BackendEndpoints.GOOGLE_LOGIN, data })
  }

  async logout () {
    return this._apiGet({ url: BackendEndpoints.GOOGLE_LOGOUT })
  }

  async fetchMapDrivers (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_DRIVERS, data })
  }

  async fetchMapTrip (params) {
    return this._apiGet({ url: BackendEndpoints.MAP_TRAVEL, params })
  }

  async fetchTripAdditionalStatistics (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_TRAVEL_ADDITIONAL_STATISTICS, data })
  }

  async fetchCriticalAdditionalStatistics (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_CRITICAL_ADDITIONAL_STATISTICS, data })
  }

  async fetchDriverProfile (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_DRIVER_PROFILE, data })
  }

  async fetchDriverTripList (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_DRIVER_TRIPS, data })
  }

  async sendUserFeedback (data) {
    // TODO: rewrite backend part for proper errors
    return this._apiPost({ url: BackendEndpoints.MAP_USER_FEEDBACK, data })
  }

  async checkCriticalVideoExistence (params) {
    return this._apiGet({ url: BackendEndpoints.MAP_CHECK_CRITICAL_VIDEO, params })
  }

  async checkCriticalVideoFps (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_CHECK_CRITICAL_VIDEO_FPS, data })
  }

  async criticalEventConfirmation (data) {
    // TODO: rewrite backend part for proper errors
    return this._apiPost({ url: BackendEndpoints.MAP_CRITICAL_EVENT_CONFIRMATION, data })
  }

  async setAllCameraCorrectTrip (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_SET_ALL_CAMERA_CORRECT_TRIP, data })
  }

  async setAllMaskWearingTrip (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_SET_ALL_MASK_WEARING_TRIP, data })
  }

  async fetchDefaultSettings () {
    return this._apiGet({ url: BackendEndpoints.SETTINGS_FETCH_DEFAULT })
  }

  async setDefaultSettings (settings) {
    return this._apiPost({ url: BackendEndpoints.SETTINGS_SET_DEFAULT, data: settings })
  }

  async setDriverSettings (settings) {
    return this._apiPost({ url: BackendEndpoints.MAP_SET_DRIVER_PROFILE, data: settings })
  }

  async syncDriversWithDefaultSettings (data) {
    return this._apiPost({ url: BackendEndpoints.SETTINGS_SYNC_DRIVERS, data })
  }

  async fetchNanoDriverList () {
    return this._apiPost({ url: BackendEndpoints.MAP_NANO_DRIVER_LIST })
  }

  async fetchDriverList () {
    return this._apiPost({ url: BackendEndpoints.MAP_DRIVER_LIST })
  }

  async downloadTripStatistics (data) {
    return this._apiPost({ url: BackendEndpoints.MAP_REQUEST_STATISTICS, type: 'download', data })
  }

  async downloadVideoArchive (params) {
    return this._apiGet({ url: BackendEndpoints.MAP_LOAD_CRITICAL_ARCHIVE, type: 'download', params })
  }

  async loadCriticalEventsChart (data) {
    return this._apiPost({ url: BackendEndpoints.CHARTS_LOAD_CRITICAL_EVENTS, data })
  }

  async loadCriticalEventsChartDoughNut (data) {
    return this._apiPost({ url: BackendEndpoints.CHARTS_LOAD_CRITICAL_EVENTS_DOUGHNUT, data })
  }

  async downloadSummaryReport (data) {
    return this._apiPost({ url: BackendEndpoints.REPORTS_LOAD_SUMMARY, type: 'download', data })
  }

  async downloadSlidesArchive (data) {
    return this._apiPost({ url: BackendEndpoints.REPORTS_SLIDES_ARCHIVE, type: 'download', data })
  }

  async downloadCriticalDatasetReport (data) {
    return this._apiPost({ url: BackendEndpoints.REPORTS_LOAD_CRITICAL_DATASET, type: 'download', data })
  }

  // CHARTS_LOAD_DRIVERS
  async fetchChartDriverList (data = {}) {
    return this._apiPost({ url: BackendEndpoints.CHARTS_LOAD_DRIVERS, data })
  }

  async fetchDatasetViewerData (data = {}) {
    return this._apiPost({ url: BackendEndpoints.DATASET_LOAD_DATA, data })
  }

  async loadDemoVideos (data = {}) {
    return this._apiPost({ url: BackendEndpoints.DEMO_VIDEOS, data })
  }

  async getPapersList () {
    return this._apiGet({ url: BackendEndpoints.PAPERS })
  }

  async getRusavicVideoList () {
    return this._apiGet({ url: BackendEndpoints.RUSAVIC_VIDEO_LIST })
  }

  async getUserProfile () {
    return this._apiGet({ url: BackendEndpoints.USER_PROFILE })
  }

  async processCriticalDatasetEntry (data = {}) {
    return this._apiPost({ url: BackendEndpoints.SET_CRITICAL_DATASET_ENTRY, data })
  }

  async setTripReportStatus (data = {}) {
    return this._apiPost({ url: BackendEndpoints.MAP_SET_TRIP_REPORT_STATUS, data })
  }

  async getTripRoadPoints (data = {}) {
    return this._apiPost({ url: BackendEndpoints.MAP_TRIP_ROAD_POINTS, data })
  }

  async checkCriticalSlides (params) {
    return this._apiGet({ url: BackendEndpoints.CHECK_CRITICAL_SLIDE, params })
  }

  async getCriticalSlide (params) {
    return this._apiGet({ url: BackendEndpoints.GET_CRITICAL_SLIDE, params })
  }

  async sendServerRequest (data = {}) {
    return this._apiPost({ url: BackendEndpoints.REQUESTS_SEND_SERVER_REQUEST, data })
  }

  async fetchClientFileData (data = {}) {
    return this._apiPost({ url: BackendEndpoints.REQUESTS_FETCH_FILE_DATA, data })
  }

  async fetchRequestsDatatable (data = {}) {
    return this._apiPost({ url: BackendEndpoints.REQUESTS_DATATABLE, data })
  }

  async getCarParks () {
    return this._apiGet({ url: BackendEndpoints.GET_CAR_PARKS })
  }

  async editCarPark (data = {}) {
    return this._apiPost({ url: BackendEndpoints.EDIT_CAR_PARK, data })
  }

  async removeCarPark (data = {}) {
    return this._apiPost({ url: BackendEndpoints.REMOVE_CAR_PARK, data })
  }

  async editCarParkDriver (data = {}) {
    return this._apiPost({ url: BackendEndpoints.EDIT_CAR_PARK_DRIVER, data })
  }

  async removeCarParkDriver (data = {}) {
    return this._apiPost({ url: BackendEndpoints.REMOVE_CAR_PARK_DRIVER, data })
  }

  async searchCarParkDrivers (data) {
    return this._apiPost({ url: BackendEndpoints.SEARCH_CAR_PARK_DRIVERS, data })
  }
}

export default new BackendProvider()
